import { ukDayTime } from "./time";

/**
 * The one status language for the Hub and every partner portal — nine core stages, the same words everywhere:
 * Submitted → With lenders → Under assessment → Awaiting documents → Preparing offer → Offer issued →
 * Offer accepted → Funded, or Declined. Awaiting documents is automatic while a document request is open;
 * Preparing offer is the desk's call once every document it asked for is in.
 * What is happening inside a stage is never a stage: a desk posts a Right now update (Trying to reach the client,
 * Call back booked, With our credit team, Waiting on valuation or searches) and it shows on the stage card.
 * A stage is a coloured box with a small square dot and the word; hovering it says what is happening right now.
 *
 * Colour: green means money, amber means someone is being waited on, brand blue with a soft
 * breathing dot means "look at me" (the owner retired red in Oct 2026), and Declined is a calm grey.
 */
export const CHIP = {
  ink: "bg-[#0B1220] text-white border-[#0B1220]",
  amber: "bg-[#FFEFC4] text-[#7A4800] border-[#EFC35B]",
  blue: "bg-[#E4ECFF] text-[#0A3DB8] border-[#A9C2FF]",
  teal: "bg-[#CCFBF1] text-[#0F766E] border-[#5EEAD4]",
  mint: "bg-[#D8F2E9] text-[#085B43] border-[#8BD6BF]",
  acc: "bg-[#C7EFDF] text-[#064E3B] border-[#6FCFAC]",
  green: "bg-[#0B6B4F] text-white border-[#0B6B4F]",
  red: "bg-[#F1F5F9] text-[#64748B] border-[#CBD5E1]",
  attn: "bg-[#EFF4FF] text-[#0044CC] border-[#A9C2FF]",
  info: "bg-[#FFF6E0] text-[#8A5200] border-[#EFCB82]",
  quiet: "bg-[#FFF3DC] text-[#8A5200] border-[#E8BE7E]",
  grow: "bg-[#E7F8EE] text-[#08663A] border-[#8FDDB3]",
  reach: "bg-[#E9F1FF] text-[#0040C1] border-[#A9C2FF]",
  slate: "bg-slate-50 text-slate-600 border-slate-200",
};

export const DOT = {
  ink: "#0B1220", amber: "#EFC35B", blue: "#0A3DB8", teal: "#14B8A6", mint: "#0D9488", acc: "#0A7D5A",
  green: "#0B6B4F", red: "#94A3B8", attn: "#0055FF", info: "#D98C00", quiet: "#E8BE7E", slate: "#CBD5E1",
  grow: "#12B76A", reach: "#0055FF",
};

/** Stage as the introducer and the Hub read it — identical words to the desk's own. `ring` picks the status ring. */
export const STAGE = {
  new: { label: "Submitted", ring: "new", note: "being prepared by Connexis", tone: "ink" },
  sent: { label: "With lenders", ring: "sent", note: "waiting to be opened", tone: "ink" },
  in_review: { label: "Under assessment", ring: "review", note: "a lender is assessing it", tone: "blue" },
  awaiting_docs: { label: "Awaiting documents", ring: "docs", note: "waiting on the client's paperwork", tone: "amber" },
  preparing_offer: { label: "Preparing offer", ring: "preparing", note: "terms being drawn up", tone: "teal" },
  offer_made: { label: "Offer issued", ring: "offer", note: "terms with the client", tone: "mint" },
  offer_accepted: { label: "Offer accepted", ring: "accepted", note: "funds being arranged", tone: "acc" },
  completed: { label: "Funded", ring: "funded", note: "funds released", tone: "green" },
  declined: { label: "Declined", ring: "declined", note: "every lender passed", tone: "red" },
};

/** One desk's lane — as the desk holding it reads it, and as a lane on the Hub and introducer screens. */
export const DESK_STAGE = {
  sent: { label: "With lenders", ring: "sent", note: "not opened yet", tone: "ink" },
  in_review: { label: "Under assessment", ring: "review", note: "being assessed", tone: "blue" },
  awaiting_docs: { label: "Awaiting documents", ring: "docs", note: "waiting on paperwork", tone: "amber" },
  preparing_offer: { label: "Preparing offer", ring: "preparing", note: "terms being drawn up", tone: "teal" },
  offer_made: { label: "Offer issued", ring: "offer", note: "terms with the client", tone: "mint" },
  completed: { label: "Funded", ring: "funded", note: "funds released", tone: "green" },
  declined: { label: "Declined", ring: "declined", note: "desk passed", tone: "red" },
};
export const PHASE_KEYS = ["awaiting_docs", "preparing_offer"];
/** A lane between Under assessment and an offer — Awaiting documents while paperwork is open, then Preparing offer. */
export const phaseOf = (r, docs = r?.docs_waiting) => {
  if (!["sent", "in_review"].includes(r?.status || "sent") || r?.withdrawn) return null;
  if ((docs || 0) > 0) return "awaiting_docs";
  return r?.preparing_offer_at ? "preparing_offer" : null;
};
const casePhase = (lead) => {
  if (!["sent", "in_review"].includes(lead?.stage)) return null;
  if (lead?.phase !== undefined) return lead.phase || null;
  const ps = (lead?.routes || []).map((r) => phaseOf(r));
  return ps.includes("preparing_offer") ? "preparing_offer" : ps.includes("awaiting_docs") ? "awaiting_docs" : null;
};
export const WITHDRAWN = { key: "withdrawn", label: "Withdrawn", ring: "withdrawn", note: "taken back by Connexis", tone: "red" };

/** Right now updates — what is happening inside Under assessment, never a stage. Waiting on / Next step are the
 *  defaults a desk can edit before posting. */
export const NOW = {
  no_contact: { key: "no_contact", label: "Trying to reach the client", waiting: "The client — to pick up or reply",
                next: "Speak to the client, then carry on the assessment", pauses: true },
  callback: { key: "callback", label: "Call back booked", waiting: "The client — to take the booked call",
              next: "Speak to the client at the booked time", pauses: true },
  credit: { key: "credit", label: "With our credit team", waiting: "The lender's credit team", next: "A credit decision, then terms" },
  valuation: { key: "valuation", label: "Waiting on valuation or searches", waiting: "The valuer or solicitors",
               next: "Results back, then a decision", pauses: true },
};
/** The menu order, and which update a case shows first when several desks posted one. */
export const NOW_KEYS = ["no_contact", "callback", "valuation", "credit"];
export const STEP_PARENT = {
  no_contact: "in_review", callback: "in_review", credit: "in_review", valuation: "in_review",
  offer_accepted: "offer_made", offer_declined: "offer_made",
};
/** How the client answered an offer. Offer accepted is a core stage; a turn-down stays Offer issued. */
export const STEP_STAGE = {
  offer_accepted: { key: "offer_accepted", label: "Offer accepted", ring: "accepted", note: "funds being arranged", tone: "acc" },
  offer_declined: { key: "offer_declined", label: "Offer issued", ring: "offer", note: "client turned the terms down", tone: "mint" },
};
export const stepOf = (r) => (r?.step && STEP_PARENT[r.step] === (r.status || "sent") ? r.step : null);
export const nowOf = (r) => {
  const k = stepOf(r);
  const d = NOW[k];
  return d ? { key: k, label: d.label, waiting: r.now_waiting || d.waiting, next: r.now_next || d.next,
               callback_at: r.callback_at, at: r.now_at || r.step_at, pauses: !!d.pauses } : null;
};

export const QUIET_DAYS = 7;
export const OPEN_STAGES = ["new", "sent", "in_review", "offer_made"];

export const daysSince = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 86400000);
};

export const isOpen = (stage) => OPEN_STAGES.includes(stage || "new");

/** A desk opened the lane and did something real on it (note, document request, chase) in the last 48h. */
export const WORKING_HOURS = 48;
export const isWorking = (r) => {
  if (!["sent", "in_review"].includes(r?.status || "sent") || !r?.last_worked_at) return false;
  const t = new Date(r.last_worked_at).getTime();
  return !Number.isNaN(t) && Date.now() - t < WORKING_HOURS * 3600000;
};
export const inProgress = (lead) => ["sent", "in_review"].includes(lead?.stage) && (lead?.routes || []).some(isWorking);
/* ---------- who moved a stage, when and how — read by the stage card ----------
   Each screen's payload is already masked for its viewer: the Hub's lanes carry the desk's name (and the person
   who pressed the button), the introducer's carry "Desk A", and a desk's own row is read as "you". */
const latest = (xs, key) => xs.filter((x) => x?.[key]).sort((a, b) => (a[key] < b[key] ? 1 : -1))[0];

const howOf = (via, self) => ({
  connie: self ? "By Connie, on your behalf" : "By Connie, on the desk's behalf",
  crm: self ? "Synced from your CRM" : "Synced from the desk's own CRM",
  connexis: "By the Connexis desk",
  auto: "Automatically, after no reply",
}[via] || (self ? "In your portal" : "In the lender portal"));

/** One lane's last stage move. `self` = the desk reading its own lane. */
export function laneMove(r, { self = false } = {}) {
  if (!r) return null;
  const desk = self ? "Your desk" : r.company_name || r.alias || "The desk";
  const person = r.status_by && !["connie", "crm", "connexis", "auto"].includes(r.status_via)
    ? (self ? r.status_by : `${r.status_by} · ${desk}`) : null;
  if (r.withdrawn) {
    return { who: "Connexis", at: r.withdrawn_at || r.status_updated_at,
             how: r.status_via === "auto" ? "Automatically, after no reply" : "Taken back by the Connexis desk" };
  }
  const step = stepOf(r);
  if (step) return { who: person || desk, at: r.now_at || r.step_at || r.status_updated_at, how: howOf(r.status_via, self) };
  const ph = phaseOf(r);
  if (ph === "preparing_offer") {
    return r.preparing_offer_by === "auto" ? { who: "Connexis", at: r.preparing_offer_at, how: "Automatically, once every document was in" }
      : { who: person || desk, at: r.preparing_offer_at || r.status_updated_at, how: howOf(r.status_via, self) };
  }
  if (ph === "awaiting_docs") return { who: desk, at: r.last_worked_at || r.status_updated_at, how: "Automatically, while documents are requested" };
  if (isWorking(r)) return { who: desk, at: r.last_worked_at, how: "Automatically, while the desk works on it" };
  if ((r.status || "sent") === "sent") return { who: "Connexis", at: r.sent_at, how: self ? "Sent to you by Connexis" : "Sent to the desk by Connexis" };
  if (r.status_via === "connexis") return { who: "Connexis", at: r.status_updated_at, how: "Put back by the Connexis desk" };
  return { who: person || desk, at: r.status_updated_at, how: howOf(r.status_via, self) };
}

/** The case's last stage move — the lane that put it where it is now. */
function caseMove(lead, state) {
  const routes = lead?.routes || [];
  const k = state?.key;
  if (!routes.length || (lead?.stage || "new") === "new") {
    return { who: lead?.submitted_by_name || lead?.source_name || "Connexis", at: lead?.created_at,
             how: lead?.customer_submitted ? "The client applied online"
               : lead?.source_type === "referral" ? "Submitted in the introducer portal" : "Opened by the Connexis desk" };
  }
  let lane = null;
  if (state?.now) lane = latest(routes.filter((r) => stepOf(r) === state.now.key), "step_at");
  else if (STEP_STAGE[k]) lane = latest(routes.filter((r) => stepOf(r) === k), "step_at");
  else if (state?.working) lane = latest(routes.filter(isWorking), "last_worked_at");
  else if (k === "sent" || lead.stage === "sent") lane = latest(routes.filter((r) => (r.status || "sent") === "sent"), "sent_at");
  else lane = latest(routes.filter((r) => r.status === lead.stage), "status_updated_at");
  const m = lane ? laneMove(lane) : null;
  if (lead.admin_override && lead.stage_set_at && (!m?.at || lead.stage_set_at >= m.at)) {
    return { who: "Connexis", at: lead.stage_set_at, how: "Set by hand by the Connexis desk" };
  }
  if (m && (k === "sent" || lead.stage === "sent")) {
    const n = routes.length;
    return { ...m, at: latest(routes, "sent_at")?.sent_at || m.at, how: `Sent to ${n} desk${n === 1 ? "" : "s"} by Connexis` };
  }
  return m || { who: "Connexis", at: lead.updated_at, how: "By the Connexis desk" };
}

/** One lane's chip — Withdrawn, then its Right now update or the client's answer, then "being worked on", then the status. */
export const laneStage = (r) => {
  const moved = laneMove(r);
  const s = stepOf(r);
  const now = nowOf(r);
  const ctx = { level: "lane", move: r?.move, offer: r?.offer, callback_at: r?.callback_at, reason: r?.decline_reason };
  const ph = phaseOf(r);
  if (r?.withdrawn) return { ...WITHDRAWN, moved, ctx };
  if (now) return { ...DESK_STAGE[ph || "in_review"], key: ph || "in_review", live: true, now, moved, ctx };
  if (s) return { ...STEP_STAGE[s], moved, ctx };
  if (ph) return { ...DESK_STAGE[ph], key: ph, live: true, moved, ctx: { ...ctx, docs: r?.docs_waiting || 0 } };
  return isWorking(r) ? { ...DESK_STAGE.in_review, key: "in_review", note: "being worked on now", live: true, working: true, moved, ctx }
    : { ...(DESK_STAGE[r?.status] || DESK_STAGE.sent), key: r?.status || "sent", moved, ctx };
};

export const agoLine = (iso) => {
  const h = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 3600000));
  return h < 1 ? "just now" : h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
};
const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

/** What a case should show on a row — the furthest any desk has taken it, in the shared words. */
export function caseState(lead) {
  const st = baseCaseState(lead);
  const routes = lead?.routes || [];
  const offer = routes.find((r) => r.status === "offer_made" && r.offer && stepOf(r) !== "offer_declined")?.offer;
  const callback_at = routes.filter((r) => stepOf(r) === "callback").map((r) => r.callback_at).filter(Boolean).sort()[0];
  const ctx = { level: "case", docs: lead?.docs_open || 0, move: lead?.move, offer, callback_at,
                auto_prep: routes.some((r) => phaseOf(r) === "preparing_offer" && r.preparing_offer_by === "auto"),
                n: routes.filter((r) => r.status !== "declined").length };
  return { ...st, moved: caseMove(lead, st), ctx };
}

function baseCaseState(lead) {
  const stage = lead?.stage || "new";
  const base = STAGE[stage] || STAGE.new;
  const routes = lead?.routes || [];
  const live = routes.filter((r) => r.status !== "declined");
  const withStep = (k) => live.filter((r) => stepOf(r) === k);
  if (stage === "offer_made") {
    if (withStep("offer_accepted").length) return { ...STEP_STAGE.offer_accepted, stage, sub: "The client said yes to the terms" };
    const offers = live.filter((r) => r.status === "offer_made");
    if (offers.length && offers.every((r) => stepOf(r) === "offer_declined")) {
      const others = live.length - offers.length;
      return { ...STEP_STAGE.offer_declined, stage,
               sub: others ? `${plural(others, "other desk")} still on it` : "The desk can come back with new terms" };
    }
  }
  const ph = casePhase(lead);
  if (["sent", "in_review"].includes(stage)) {
    for (const k of NOW_KEYS) {
      const on = withStep(k);
      if (!on.length) continue;
      const lane = k === "callback" ? [...on].sort((a, b) => ((a.callback_at || "") < (b.callback_at || "") ? -1 : 1))[0]
        : latest(on, "step_at") || on[0];
      const now = nowOf(lane);
      const sub = k === "callback" ? `Right now: call back booked for ${ukDayTime(now.callback_at, "a set time")}`
        : `Right now: ${now.label.toLowerCase()}${on.length > 1 ? ` · ${on.length} desks` : ""}`;
      return { ...STAGE[ph || "in_review"], key: ph || "in_review", stage, live: true, now, sub };
    }
    if (ph === "awaiting_docs") {
      const n = lead?.docs_open || 0;
      return { ...STAGE.awaiting_docs, key: ph, stage, live: true, sub: n ? `${n} document request${n === 1 ? "" : "s"} open` : "Waiting on the client's paperwork" };
    }
    if (ph === "preparing_offer") {
      const auto = routes.some((r) => phaseOf(r) === "preparing_offer" && r.preparing_offer_by === "auto");
      return { ...STAGE.preparing_offer, key: ph, stage, live: true, sub: auto ? "Every document in · terms on the way" : "Terms on the way" };
    }
  }
  const working = routes.filter(isWorking);
  if (inProgress(lead)) {
    const last = working.map((r) => r.last_worked_at).sort().pop();
    return { ...STAGE.in_review, key: "in_review", note: "being worked on now", stage, live: true, working: true,
             sub: `${working.length === 1 ? "" : `${working.length} desks · `}Last move ${agoLine(last)}` };
  }
  const idle = daysSince(lead?.updated_at);
  if (isOpen(stage) && stage !== "new" && idle !== null && idle >= QUIET_DAYS) {
    return { key: "quiet", label: base.label, ring: base.ring, note: "we're chasing it", tone: base.tone, sub: `Quiet for ${idle} days`, stage };
  }
  const passed = routes.filter((r) => r.status === "declined" && !r.withdrawn).length;
  const sub = {
    new: "Being prepared by Connexis",
    sent: `Sent to ${routes.length || "the"} lender${routes.length === 1 ? "" : "s"}`,
    in_review: `${routes.filter((r) => r.status === "in_review").length || 1} desk assessing`,
    offer_made: `${routes.filter((r) => r.status === "offer_made" && !stepOf(r)).length || 1} desk offered`,
    completed: "Fee confirmed",
    declined: passed ? `${passed} desk${passed === 1 ? "" : "s"} declined` : "Finding it a new home",
  }[stage];
  const takenBack = stage === "declined" && routes.length > 0 && routes.every((r) => r.withdrawn);
  return { key: stage, label: base.label, ring: base.ring, note: takenBack ? WITHDRAWN.note : base.note, tone: base.tone, sub, stage };
}

/** Which filter chip a case sits under — the extra stages count inside the stage they belong to. */
export const caseFilterKey = (lead) => {
  const k = caseState(lead).key;
  if (k === "quiet") return lead.stage === "sent" ? "new" : lead.stage;
  if (k === "sent") return "new";
  return k === "offer_declined" ? "offer_made" : k;
};

/** A desk's own row: soft blue "Due now" once it is past the turnaround it promised us. */
export function deskState(row, turnaroundDays = 3) {
  const st = baseDeskState(row, turnaroundDays);
  const ctx = { level: "desk", docs: row?.docs_open || 0, move: row?.move, offer: row?.offer, callback_at: row?.callback_at,
                auto_prep: row?.preparing_offer_by === "auto",
                turnaround: Math.max(1, Number(turnaroundDays) || 3), reason: row?.decline_reason };
  const moved = st.key === "overdue"
    ? { who: "Connexis", how: "Automatically, once your turnaround passed",
        at: row?.sent_at ? new Date(new Date(row.sent_at).getTime() + Math.max(1, Number(turnaroundDays) || 3) * 86400000).toISOString() : null }
    : laneMove({ ...row, status: row?.status || "sent" }, { self: true });
  return { ...st, moved, ctx };
}

function baseDeskState(row, turnaroundDays = 3) {
  const status = row?.status || "sent";
  const base = DESK_STAGE[status] || DESK_STAGE.sent;
  const waiting = daysSince(row?.sent_at);
  const step = stepOf({ ...row, status });
  const now = nowOf({ ...row, status });
  const ph = phaseOf({ ...row, status }, row?.docs_open);
  if (now) {
    const sub = now.key === "callback" ? `Right now: call back booked for ${ukDayTime(now.callback_at, "a set time")}`
      : `Right now: ${now.label.toLowerCase()}${now.pauses ? " · clock paused" : ""}`;
    return { ...DESK_STAGE[ph || "in_review"], key: ph || "in_review", live: true, now, status, sub };
  }
  if (ph === "awaiting_docs") {
    return { ...DESK_STAGE.awaiting_docs, key: ph, live: true, status,
             sub: `${row.docs_open} document request${row.docs_open === 1 ? "" : "s"} open` };
  }
  if (ph === "preparing_offer") {
    return { ...DESK_STAGE.preparing_offer, key: ph, live: true, status,
             sub: row?.preparing_offer_by === "auto" ? "Every document in · send your terms" : "Send your terms" };
  }
  if (step) {
    const sub = { offer_accepted: "The client said yes · arranging the funds", offer_declined: "Client turned it down · send new terms, or pass" }[step];
    return { ...STEP_STAGE[step], status, sub };
  }
  if (isWorking({ ...row, status })) {
    return { ...DESK_STAGE.in_review, key: "in_review", note: "you're working it", live: true, working: true, status,
             sub: row?.docs_open > 0 ? `${row.docs_open} document${row.docs_open === 1 ? "" : "s"} outstanding`
               : `Last move ${agoLine(row.last_worked_at)}` };
  }
  const note = { sent: "ready for you", declined: "you passed" }[status] || base.note;
  const ring = base.ring;
  if (row?.sla?.state === "waiting_client" && ["sent", "in_review"].includes(status)) {
    return { key: status, label: base.label, ring, note, tone: base.tone, sub: "Waiting on the client", status };
  }
  const late = row?.sent_at && Date.now() - new Date(row.sent_at).getTime() > Math.max(1, Number(turnaroundDays) || 3) * 86400000;
  if (status === "sent" && (late || ["chasing", "overdue"].includes(row?.sla?.state))) {
    return { key: "overdue", label: "Due now", ring: "due", note: "past your turnaround", tone: "attn", live: true, sub: `Waiting ${waiting} days`, status };
  }
  if (["sent", "in_review"].includes(status) && row?.docs_open > 0) {
    return { key: status, label: base.label, ring, note, tone: base.tone,
             sub: `${row.docs_open} document${row.docs_open === 1 ? "" : "s"} outstanding`, status };
  }
  if (status === "declined" && (row?.withdrawn || row?.withdrawn_reason)) {
    return { ...WITHDRAWN,
             sub: row?.withdrawn_reason ? `We took it back — ${row.withdrawn_reason.toLowerCase()}` : "Connexis took it back",
             status };
  }
  const sub = {
    sent: waiting ? `Arrived ${waiting} day${waiting === 1 ? "" : "s"} ago` : "Arrived today",
    in_review: "You're assessing it",
    offer_made: "Your terms are with the client",
    completed: "Fee invoiced",
    declined: row?.decline_reason || "You declined it",
  }[status];
  return { key: status, label: base.label, ring, note, tone: base.tone, sub, status };
}

/** The progress bar — Submitted → With lenders → Under assessment → Awaiting documents → Preparing offer →
 *  Offer issued → Offer accepted → Funded. The rung the case is on carries its note — or the Right now update —
 *  and breathes while live. */
// The ladder's own words — short so eight fit side by side on any screen; the full stage name shows on hover.
const RUNGS = ["Submitted", "With lenders", "Assessing", "Documents", "Offer prep", "Offer issued", "Accepted", "Funded"];
const RUNG_FULL = ["Submitted", "With lenders", "Under assessment", "Awaiting documents", "Preparing offer", "Offer issued", "Offer accepted", "Funded"];
export function ladderFor(stage, state) {
  const assessing = stage === "in_review" || (stage === "sent" && (state?.working || state?.now || PHASE_KEYS.includes(state?.key)));
  const at = state?.key === "awaiting_docs" ? 3 : state?.key === "preparing_offer" ? 4 : assessing ? 2
    : { new: 0, sent: 1, offer_made: state?.key === "offer_accepted" ? 6 : 5, completed: 7 }[stage];
  const note = state?.now ? state.now.label : state?.note;
  const noDocs = state?.key === "preparing_offer" && !state?.ctx?.auto_prep;  // went straight there — no paperwork asked for
  return RUNGS.map((label, i) => {
    const r = rung(label, i);
    return { ...r, full: r.label === label ? RUNG_FULL[i] : r.label };
  });

  function rung(label, i) {
    if (stage === "completed") return { label, state: "d", note: i === 7 ? note : undefined };
    if (noDocs && i === 3) return { label, state: "s" };
    if (stage === "declined") return { label: i === 2 ? state?.label || "Declined" : label, state: i < 2 ? "d" : i === 2 ? "x" : "", note: i === 2 ? note : undefined };
    const now = i === at;
    return { label, state: i < at ? "d" : now ? "n" : "", live: now && !!state?.live,
             note: now ? note : undefined, ring: now ? state?.ring : undefined };
  }
}

export const gbp = (v, { compact = false } = {}) => {
  const n = Math.round(Number(v || 0));
  if (compact && Math.abs(n) >= 1_000_000) return `£${(n / 1_000_000).toFixed(n >= 10_000_000 ? 1 : 2)}m`;
  if (compact && Math.abs(n) >= 10_000) return `£${Math.round(n / 1000)}k`;
  return `£${n.toLocaleString("en-GB")}`;
};
